"use client";

import type {
  PublicAssessmentBoard,
  PublicBoardEntry,
} from "@repo/api/use-cases/assessments/public-board";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/ui";

import { durationLabel, initial } from "@/lib/assessments/board-format";
import { api } from "@/lib/trpc/react";

/** "n/a" is an attempt with no observed duration -- see `elapsedMinutes`. */
function minutesLabel(minutes: number | null): string {
  return minutes === null ? "n/a" : durationLabel(minutes);
}

/**
 * The highest rarity on the board, which is what the bars are drawn against.
 *
 * Not the first row's. An unranked board is sorted by name, so reading the
 * baseline off the top row scaled every bar against whoever happened to sort
 * first and left bars disagreeing with the numbers beside them.
 *
 * Rarity rather than precision because rarity is what the order is made on, and
 * a bar that measured something else would put a long bar above a short one.
 * Kept as a maximum rather than a fixed ceiling because a rarity sum is unbounded
 * above: there is no 100% to draw against.
 */
export function topRarity(
  entries: readonly Pick<PublicBoardEntry, "rarity">[],
): number | null {
  return entries.reduce<number | null>(
    (best, entry) =>
      entry.rarity !== null && (best === null || entry.rarity > best)
        ? entry.rarity
        : best,
    null,
  );
}

/**
 * Precision as whole percent.
 *
 * Only called for a scored row. An unscored one has no precision to label and
 * the cell says "pending" instead, which is a different statement from "n/a" --
 * the latter is for a scored attempt that was left unranked.
 */
function precisionLabel(precision: number | null): string {
  return precision === null ? "n/a" : `${String(Math.round(precision * 100))}%`;
}

/**
 * Rarity to two places.
 *
 * Not a percentage: it is a sum of -log2(k/n) over somebody's confirmed novel
 * findings, so it has no ceiling and a reader who saw "82%" would take it for
 * one. "n/a" is a scored attempt with no cluster run behind it yet, which is
 * every attempt until an operator has partitioned the findings.
 */
function rarityLabel(rarity: number | null): string {
  return rarity === null ? "n/a" : rarity.toFixed(2);
}

/**
 * The board, and the numbers a reader needs to check it.
 *
 * Every column is a fact about one attempt rather than a claim about a person:
 * what it submitted, what held up, how long it took, and how many findings its
 * author went on to rule on for other people. Rarity is last because it is what
 * the order is made on, so the bar and the sequence agree by construction, and
 * precision sits beside it as the figure that breaks its ties.
 *
 * Findings themselves are never here. What somebody wrote goes to two checkers
 * and stays there.
 */
export function BoardPanel({ board }: { board: PublicAssessmentBoard }) {
  /*
   * Read after mount, like `LiveSittingsPanel` on this same page.
   *
   * The route is statically rendered and shared by every visitor, so who is
   * reading it cannot be known when the HTML is built. Fetching it here leaves
   * the cached markup identical for everybody and adds the row once the answer
   * arrives; a signed-out visitor gets null and no request is wasted on a board
   * that is not published.
   */
  const { data: myRow } = api.assessments.myRowOnBoard.useQuery(
    { slug: board.slug },
    { enabled: board.resultsPublished, staleTime: 1000 * 60 * 5 },
  );
  const myAttemptId = myRow?.entry.attemptId ?? null;
  const published = board.resultsPublished && board.entries.length > 0;

  if (!published) {
    return <EmptyBoard board={board} />;
  }

  const placed = board.entries.map((entry, index) => ({
    entry,
    // Gated on rarity, which is what the order is made on. Gating on precision
    // numbered a row whose position came from the tiebreak, so a reader checking
    // the sequence against the column beside it found n/a. No number for an
    // attempt scoring left unranked either, and the cell still renders so the
    // columns stay aligned under the header.
    rank: board.ranked && entry.rarity !== null ? index + 1 : null,
  }));

  const leader = topRarity(board.entries);

  /*
   * The reader's own row, when the published rows do not include it.
   *
   * Appended under the table rather than sorted into it: the order the board
   * publishes is not rearranged for one visitor, and the rank it carries is its
   * place in the whole exam rather than the position it is rendered in. It comes
   * from `myRowOnBoard` because the page's payload stops at the cap, so on a
   * cohort larger than that the row is not here to find.
   */
  const pinned = placed.some((row) => row.entry.attemptId === myAttemptId)
    ? null
    : myRow;
  /*
   * Whether the rows are a slice of the cohort or the whole of it.
   *
   * Both conditions are needed. Short of the cap the table holds everybody it
   * could, and at the cap with nothing behind it there is nothing withheld to
   * disclose; only a full page with a larger cohort behind it is a slice. The
   * cohort figure is on the tab beside this one either way, so a table that
   * names twenty-five people has to say which of the two a reader is looking at.
   */
  const capped =
    board.entries.length >= board.entryLimit &&
    board.participants > board.entries.length;
  // Three reasons a board carries no sequence, and they answer three different
  // questions. Nothing scored: why does nobody have a result. Scored but not
  // grouped: why is there no order when there are results. Grouped but short of
  // the floor: why is the order not numbered.
  const anyScored = board.entries.some((entry) => entry.scored);
  const anyRarity = board.entries.some((entry) => entry.rarity !== null);

  return (
    <div>
      {/* Only where the table cannot speak for itself: a ranked board has a
          Rank column and needs no caption, while an unranked one is missing
          that column and would otherwise read as a ranking somebody forgot to
          number. */}
      {board.ranked ? null : (
        <p className="assess-sub text-marketing-text-muted mb-7 max-w-[640px]">
          {!anyScored
            ? "This run is still open. Everybody here has submitted, and their findings are with the checkers. Nothing on this board is a rank yet."
            : anyRarity
              ? "Too few scored attempts to rank. Everybody here sat the exam."
              : "Results are in, but the findings have not been grouped yet. Until they are, there is no way to tell what somebody found that nobody else did, so this board carries results without an order."}
        </p>
      )}

      {/* Horizontal scroll on the table alone: the page body must never scroll
          sideways, and the numeric columns cannot usefully be stacked. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] border-collapse text-left">
          <thead>
            <tr className="assess-label text-marketing-text-secondary border-marketing-border-light border-b">
              {board.ranked ? (
                <th scope="col" className="w-14 pb-3 font-normal">
                  Rank
                </th>
              ) : null}
              <th scope="col" className="pb-3 font-normal">
                Expert
              </th>
              <th scope="col" className="w-28 pb-3 font-normal">
                Confirmed
              </th>
              <th scope="col" className="w-24 pb-3 font-normal">
                Reviews
              </th>
              <th scope="col" className="w-24 pb-3 font-normal">
                Time
              </th>
              <th scope="col" className="w-24 pb-3 font-normal">
                Precision
              </th>
              <th scope="col" className="w-44 pb-3 pl-4 font-normal">
                Rarity
              </th>
            </tr>
          </thead>
          <tbody>
            {placed.map((row) => (
              <BoardRow
                entry={row.entry}
                key={row.entry.attemptId}
                leader={leader}
                mine={row.entry.attemptId === myAttemptId}
                rank={row.rank}
                ranked={board.ranked}
              />
            ))}
            {pinned ? (
              <BoardRow
                entry={pinned.entry}
                key={pinned.entry.attemptId}
                leader={leader}
                mine
                rank={pinned.rank}
                ranked={board.ranked}
              />
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Where the rows end, since nothing else on this tab says so. The second
          sentence is the part that matters: the cap decides what is published,
          never what is measured. */}
      {capped ? (
        <p className="assess-sub text-marketing-text-muted mt-5 max-w-[640px]">
          The top {board.entryLimit} of {board.participants} who sat this exam.
          Every submitted attempt is checked, scored and placed on the same rule
          whether or not its row is here.
        </p>
      ) : null}
    </div>
  );
}

function BoardRow({
  entry,
  leader,
  mine,
  rank,
  ranked,
}: {
  entry: PublicBoardEntry;
  /** The top rarity on this board, so the bars read against each other. */
  leader: number | null;
  /** The reader's own row, marked so they can find themselves in the cohort. */
  mine?: boolean;
  /** Null where the row is on the board but holds no position. */
  rank: number | null;
  /** Whether the board prints a Rank column at all. */
  ranked: boolean;
}) {
  // Relative to the leader rather than to 1.0: on a board where nobody cleared
  // half, bars measured against a perfect score are all stubs and the ranking
  // becomes unreadable.
  const width =
    entry.rarity !== null && leader !== null && leader > 0
      ? Math.max(6, Math.round((entry.rarity / leader) * 100))
      : 0;
  const top = rank !== null && rank <= 3;

  return (
    <tr
      className={
        mine
          ? "border-marketing-border-light bg-marketing-cream/70 border-b"
          : "border-marketing-border-light border-b"
      }
    >
      {ranked ? (
        <td
          className={
            top
              ? "assess-cell text-marketing-text py-4 font-semibold"
              : "assess-cell text-marketing-text-secondary py-4"
          }
        >
          {rank}
        </td>
      ) : null}

      <td className="py-4 pr-4">
        <div className="flex items-center gap-3">
          {entry.name ? (
            <>
              <Avatar className="size-7 shrink-0">
                <AvatarImage
                  alt={entry.name}
                  className="object-cover"
                  src={entry.image ?? undefined}
                />
                <AvatarFallback className="bg-marketing-cream text-marketing-text-muted text-[10.5px] font-medium">
                  {initial(entry.name)}
                </AvatarFallback>
              </Avatar>
              <span className="assess-name text-marketing-text min-w-0 truncate">
                {entry.name}
              </span>
            </>
          ) : (
            <>
              <span
                aria-hidden
                className="border-marketing-border size-7 shrink-0 rounded-full border border-dashed"
              />
              <span className="assess-name text-marketing-text-secondary font-normal">
                Anonymous
              </span>
            </>
          )}
          {/* Says why the row is marked, and why it may be sitting out of
              sequence: the tint alone reads as an accident, and the pinned row
              lands directly under rank ten with nothing to explain the jump.
              Also the only label an anonymous row gets, which is the case where
              somebody cannot recognise themselves by name. */}
          {mine ? (
            <span className="assess-label text-marketing-text-muted border-marketing-border shrink-0 rounded-full border px-2 py-0.5">
              You
            </span>
          ) : null}
        </div>
      </td>

      {/* Two different sentences in one column, because there are two states a
          row can be in and the difference is the whole point of the caption
          above. Scored: how many of your findings held up. Unscored: how many
          cleared Gate 1, which is the only thing decided about them so far, and
          `title` says so for anybody who wonders why there is no confirmed
          count beside it. */}
      <td className="assess-cell text-marketing-text py-4">
        {entry.scored ? (
          <>
            <span className="font-medium">{entry.confirmed}</span>
            <span className="text-marketing-text-muted">
              {" "}
              of {entry.submitted}
            </span>
          </>
        ) : (
          <span
            className="text-marketing-text-muted"
            title="Submitted and awaiting peer review"
          >
            {entry.specified} of {entry.submitted} cleared
          </span>
        )}
      </td>
      <td className="assess-cell text-marketing-text-muted py-4">
        {entry.reviews}
      </td>
      <td className="assess-cell text-marketing-text-muted py-4">
        {minutesLabel(entry.minutes)}
      </td>

      <td
        className={
          entry.scored
            ? "assess-cell text-marketing-text py-4"
            : "assess-cell text-marketing-text-muted py-4"
        }
      >
        {entry.scored ? precisionLabel(entry.precision) : "pending"}
      </td>

      <td className="py-4 pl-4">
        <div className="flex items-center gap-3">
          <span className="bg-marketing-border-light hidden h-[3px] flex-1 overflow-hidden rounded-full sm:block">
            <span
              className={
                top
                  ? "bg-marketing-blue block h-full"
                  : "bg-marketing-text-secondary block h-full"
              }
              style={{ width: `${String(width)}%` }}
            />
          </span>
          <span
            className={
              entry.scored
                ? "assess-cell text-marketing-text w-16 shrink-0 text-right font-medium"
                : "assess-cell text-marketing-text-muted w-16 shrink-0 text-right"
            }
          >
            {entry.scored ? rarityLabel(entry.rarity) : "pending"}
          </span>
        </div>
      </td>
    </tr>
  );
}

/**
 * Results start private and widen deliberately, so an unpublished board is the
 * ordinary state rather than an error. The panel says which state it is in
 * instead of rendering empty rows that read as nobody having found anything.
 *
 * The populated case is now much wider than it was: a board fills from the first
 * submission, so reaching here at all means either the exam has not published
 * its results or nobody has sat it. Neither of those used to be distinguishable
 * from "the run has not been scored yet", which was the ordinary state and read
 * as a failure.
 */
function EmptyBoard({ board }: { board: PublicAssessmentBoard }) {
  const unpublished = !board.resultsPublished;

  return (
    <div className="border-marketing-border-light bg-marketing-surface rounded-[14px] border border-dashed px-6 py-14 text-center">
      <p className="assess-h2 text-marketing-text">
        {unpublished
          ? "This board is not published yet."
          : "Nobody has sat this exam yet."}
      </p>
      <p className="assess-sub text-marketing-text-muted mx-auto mt-2.5 max-w-[440px]">
        {unpublished
          ? "Every finding has to be ruled on before a cohort can be scored, and a board goes up once that is done rather than while it is under way."
          : "The first person to submit appears here straight away, with their result filled in once two other people have ruled on what they found."}
      </p>
    </div>
  );
}
